import type {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type { ILayerView } from "@esWebAPI/es/model/Layer";

export default class Layer {

    private readonly featureViewModel: FeatureViewModel;
    private readonly territoryModel: TerritoryModel;

    constructor(featureViewModel: FeatureViewModel, territoryModel: TerritoryModel) {
        this.featureViewModel = featureViewModel;
        this.territoryModel = territoryModel;
    }

    public getFeatureViewModel() {
        return this.featureViewModel;
    }

    /**
     * @deprecated
     */
    getTerritoryModel() {
        return this.territoryModel;
    }

    /**
     * Enable/disable a layer
     * @param layerViewId the layer to enable or disable
     * @param isEnabled enable/disable
     */
    public setEnabled(layerViewId: number, isEnabled: boolean) {
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (layer) {
            layer.setEnabled(isEnabled);
        } else {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public isEnabled(layerViewId: number) {
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (layer) {
            return layer.isEnabled();
        } else {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public isSelectable(layerViewId: number) {
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (layer) {
            return layer.isSelectable();
        } else {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public setSelectable(layerViewId: number, selectable: boolean) {
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (layer) {
            return layer.setSelectable(selectable);
        } else {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public getSelectCriteria(layerViewId: number) {
        const layer = this.featureViewModel.getLayerById(layerViewId);
        if (layer) {
            return layer.getSelectCriteria();
        } else {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public remove(layerViewId: number) {
        if (!this.featureViewModel.removeLayer(layerViewId)) {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public setIndex(layerViewId: number, index: number) {
        if (!this.featureViewModel.setIndex(layerViewId, index)) {
            throw new Error("Unknown LayerView : " + layerViewId);
        }
    }

    public getNumLayers() {
        return this.featureViewModel.getNumLayers();
    }

    public indexOf(layerViewId: number) {
        return this.featureViewModel.indexOfLayer(layerViewId);
    }

    public getById(layerViewId: number) {
        return this.featureViewModel.getLayerById(layerViewId);
    }

    public getByLayerMetaId(layerMetaId: number) {
        return this.featureViewModel.getLayerByLayerMetaId(layerMetaId);
    }

    public getByIndex(index: number) {
        return this.featureViewModel.getLayerByIndex(index);
    }

    public list() {
        return this.featureViewModel.listLayers();
    }

    public getPointLayers(includeAddressLayer: boolean = false) {
        return this.list().filter(l => l.isSelectable() && l.getGeomType() === "POINT" && (!(l.getTitle() === "Address Points" && l.getType() === "MapScopeCustomPointLayerView") || includeAddressLayer));
    }

    /**
     * Get an array of all layer view ids
     * @return an array of all layer view ids
     */
    public getLayerViewIds() {
        return this.featureViewModel.getLayerViewIds();
    }

    /**
     * Get json representation of all layerViews in the model.
     * e.g.:
     *    map.layer.getLayerViewsObjRef();
     *    map.layer.getLayerViewsObjRef().filter(lv => lv.$type == "ScheduleLayerView");
     * @returns ILayerView[]
     */
    public getLayerViewsObjRef(): ILayerView[] {
        return this.featureViewModel.listLayers().map(l => l.getLayerView());
    }

    public getLayerViewsClone(): ILayerView[] {
        return es.util.object.deepCopy(this.featureViewModel.listLayers().map(l => l.getLayerView()));
    }
}
